import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import useTrans from '@hooks/useTrans'
import { cn, getUserType, priceValutaNotation } from '@utils/safeTs'
import { OrdineDetailType, PersonalizzazioneOrdineType, RootStateType, UserType } from '@utils/types'
import { Download, Edit, EMail, MinusCircle, PlusCircle } from '@components/icons'
import { Button, Modal, StatoOrdine } from '@components/atoms'
import MyAccountArticoloOrdine from '@components/molecules/MyAccountArticoloOrdine/MyAccountArticoloOrdine'
import MyAccountPosizionePersonalizzazione from '@components/molecules/MyAccountPosizionePersonalizzazione/MyAccountPosizionePersonalizzazione'
import DatatableOrdiniDetail from '@components/molecules/DatatableOrdiniDetail/DatatableOrdiniDetail'
import styles from './MyAccountPersonalizzazioneOrdine.module.sass'
import MyAccountArticoliOrdine from '@components/molecules/MyAccountArticoliOrdine/MyAccountArticoliOrdine'
import MyAccountOrdineSectionHead from '@components/atoms/MyAccountOrdineSectionHead/MyAccountOrdineSectionHead'
import MyAccountDownloadModal from '../MyAccountDownloadModal/MyAccountDownloadModal'
import { api } from '@utils/index'
import { displayNotification } from '@components/atoms/Notifica/Notifica'
import { MYACCOUNTPERSONALIZZAZIONIORDINE } from '@utils/endpoint'
import { MyAccountPersonalizzazioneHead } from '@components/molecules'

export interface MyAccountPersonalizzazioneOrdineProps {
  ordine: OrdineDetailType
  personalizzazione: PersonalizzazioneOrdineType
  personalizzazioneFornitore?: PersonalizzazioneOrdineType
  righeVariant?: 'default' | 'onlyProducts'
  wCheckout?: boolean
  userType?: UserType
  className?: string
}

const MyAccountPersonalizzazioneOrdine = ({
  ordine,
  personalizzazione,
  personalizzazioneFornitore,
  righeVariant = 'default',
  wCheckout = false,
  userType,
  className,
}: MyAccountPersonalizzazioneOrdineProps) => {
  const t = useTrans()
  const [showPrices, setShowPrices] = useState(false)
  const [confermaSollecitoModalOpen, setConfermaSollecitoModalOpen] = useState(false)
  const [downloadModalOpen, setDownloadModalOpen] = useState(false)
  const isOrdineModificabile = !wCheckout && ordine?.statoOrdineCalcolato !== 'ORDINE_ANNULLATO' && ordine?.statoOrdineCalcolato !== 'ORDINE_EVASO' 
  const getUtente = () => ordine.ordineSg ? ordine.ordineSg?.utente : ordine.utente
  const personalizzazionePerPrezzi = userType === 'fornitore' ? personalizzazioneFornitore : personalizzazione

  const prices = [
    { key: 'totale_personalizzazione',  label: t("Importo personalizzazione"), value: personalizzazionePerPrezzi?.righeOrdine?.reduce((sum, r) => sum + r.prezzoPersonalizzazione, 0) },
    { key: 'totale_confezionamento',  label: t("Importo confezionamento"), value: personalizzazionePerPrezzi?.righeOrdine?.reduce((sum, r) => sum + r.prezzoConfezionamento, 0) },
  ].filter(x => x.value !== undefined)

  const actions = [
    // TODO - deve aprire l'editor - serve prima branch editor
    // { 
    //   key: 'modifica', 
    //   label: t("Modifica"), 
    //   icon: <Edit />, 
    //   onClick: () => console.log('TODO Modifica'),
    //   visible: isOrdineModificabile && userType !== 'fornitore' && !!personalizzazione?.posizioniPersonalizzazioneOrdine?.filter(p => p.statoPosizionePersonalizzazione !== 'BOZZA_APPROVATA')?.length, 
    // },
    { 
      key: 'download',
      label: t("Download"),
      icon: <Download />, 
      onClick: () => setDownloadModalOpen(true),
      visible: true,
    },
    { 
      key: 'invia_bozza',
      label: t("Invia bozze"), 
      icon: <EMail />, 
      href: `${ordine.caricamentoBozzeDigitaliUrl}#personalizzazione_${personalizzazione.id}`,
      visible: isOrdineModificabile && ['fornitore', 'admin'].includes(userType) && !!personalizzazione?.posizioniPersonalizzazioneOrdine?.filter(p => p.bozzaDigitale?.url && ['BOZZA_DA_CARICARE', 'BOZZA_RIFIUTATA'].includes(p.statoPosizionePersonalizzazione))?.length,
    },
    { 
      key: 'sollecita',
      label: t("Sollecita"),
       icon: <EMail />, 
       onClick: () => setConfermaSollecitoModalOpen(true),
       visible: isOrdineModificabile && ['commerciale', 'admin'].includes(userType) && !!getUtente() && !!personalizzazione?.posizioniPersonalizzazioneOrdine?.filter(p => p.bozzaDigitale?.url && ['BOZZA_INVIATA'].includes(p.statoPosizionePersonalizzazione))?.length,
    },
  ].filter(x => x.visible)

  const sendSollecito = () => {
    api
      .get(`${MYACCOUNTPERSONALIZZAZIONIORDINE}${personalizzazione.id}/send_sollecito/` )
      .then((res) => {
        displayNotification('send_sollecito_' + personalizzazione.id + '_error', t("Sollecito inviato correttamente"), 'success')
        setConfermaSollecitoModalOpen(false)
      })
      .catch(() => {
        displayNotification('send_sollecito_' + personalizzazione.id + '_error', t("Errore durante l'invio del sollecito"), 'error')
      })
  }

  return personalizzazione ? (
    <section className={cn(styles.root, styles[`root--${userType}`], className)}>
      <MyAccountPersonalizzazioneHead 
        ordine={ordine} 
        personalizzazione={personalizzazione} 
        userType={userType}
      />
      
      {/* Articoli */}
      <MyAccountArticoliOrdine 
        righeOrdine={personalizzazione.righeOrdine} 
        variant={righeVariant}
        userType={userType}
        note
        noteFornitore={ordine.noteFornitore}
      />

      {/* Posizioni */}
      {!!personalizzazione.posizioniPersonalizzazioneOrdine?.length && (
        <section className={cn(styles.section, styles['section--posizioni'])}>
          {userType !== 'cliente' && (
            <MyAccountOrdineSectionHead title={t("Posizioni")} userType={userType} />
          )}
          <div className={styles.posizioniList}>
            {personalizzazione.posizioniPersonalizzazioneOrdine.map((posizione) => (
              <MyAccountPosizionePersonalizzazione 
                key={posizione.id}
                ordine={ordine}
                personalizzazione={personalizzazione}
                posizione={posizione}
                wCheckout={wCheckout}
                userType={userType}
              />
            ))}
          </div>
        </section>
      )}

      {/* Prezzi - tutti tranne clienti */}
      {userType !== 'cliente' && !!prices.length ? (
        <section className={cn(styles.section, styles['section--prices'])}>
          <MyAccountOrdineSectionHead title={t("Prezzi")} className={styles.sectionHead} userType={userType}>
            <button
              type="button"
              className={styles.priceToggle}
              onClick={() => setShowPrices((prev) => !prev)}
              aria-expanded={showPrices}
            >
              <span>
                {showPrices
                  ? t('Nascondi prezzi')
                  : t('Espandi per visualizzare i prezzi')}
              </span>
              {showPrices ? <MinusCircle aria-hidden /> : <PlusCircle aria-hidden />}
            </button>
          </MyAccountOrdineSectionHead>
          {showPrices ? (
            <div className={styles.priceDropdown}>
              <dl className={styles.priceList}>
                {prices.map((row) => (
                  <div key={row.key} className={styles.priceRow}>
                    <dt className={styles.priceLabel}>{row.label}</dt>
                    <dd className={styles.priceValue}>{priceValutaNotation(row.value, 2)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ) : null}
        </section>
      ) : null}

      {!wCheckout && actions.length ? (
        <footer className={styles.actionBar}>
          {actions.map((action) => (
            <Button key={action.key} variant="primary" icon_pos='right' size="small" {...action} />
          ))}
        </footer>
      ) : null}

      <MyAccountDownloadModal 
        id={`download_modal_${personalizzazione.id}`}
        type="personalizzazione"
        personalizzazione={personalizzazione}
        open={downloadModalOpen}
        title={t("Download")}
        description={t("Seleziona i file che desideri scaricare")}
        onClose={() => setDownloadModalOpen(false)}
      />
      <Modal
        id={`sollecitabozze_modal_${personalizzazione.id}`}
        title={t("Invia sollecito per approvazione bozze")}
        className={styles.sollecitoModal}
        open={confermaSollecitoModalOpen}
        onClose={() => setConfermaSollecitoModalOpen(false)}
        buttons={(
          <>
            <Button
              label={t("Chiudi")}
              variant='secondary'
              onClick={() => setConfermaSollecitoModalOpen(false)}
            />
            <Button
              label={t("Conferma")}
              variant="primary"
              onClick={sendSollecito}
            />
          </>
        )}
      >
        <div className={styles.sollecitoModalDescription} dangerouslySetInnerHTML={{ __html: t("<p>Cliccando su Conferma verrà inviata un'email di sollecito al cliente che ha effettuato l'ordine per l'approvazione della bozza.</p><p>Vuoi procedere?</p>") }} />
      </Modal>
    </section>
  ) : <></>
}

export default MyAccountPersonalizzazioneOrdine
